import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { assistantApi, confirmBody, streamAssistant, uploadFile } from './api';
import { conversationMatchesWorkspace, IAssistantEntity } from './launcher';
import { tagLastAssistantMessage, withFeedback } from './transcript';
import {
    IAssistantStatus,
    IConversation,
    IConversationSummary,
    IFeedback,
    IPendingAction,
    ITranscriptMessage,
    IUpload,
    TAssistantEvent,
} from './types';

export type TActivityPhase = 'idle' | 'thinking' | 'tool' | 'answering';

export interface IActivity {
    phase: TActivityPhase;
    label: string;
    startedAt: number;
    steps: number;
}

export interface IAssistantState {
    status: IAssistantStatus | null;
    conversation: IConversation | null;
    history: IConversationSummary[];
    streaming: boolean;
    pending: IPendingAction[] | null;
    error: string | null;
    activity: IActivity;
    /** Files uploaded and waiting to go with the next message. */
    attachments: IUpload[];
    uploading: boolean;
}

const IDLE: IActivity = { phase: 'idle', label: '', startedAt: 0, steps: 0 };
/** Same marker the backend uses to render a user turn as a system line. */
export const SYSTEM_NOTE_PREFIX = '[dashboard] ';
export const WORKSPACE_MISMATCH = 'This conversation belongs to another workspace. Start a new conversation to work here.';

const appendAssistantText = (messages: ITranscriptMessage[], text: string): ITranscriptMessage[] => {
    const last = messages[messages.length - 1];
    if (last && last.role === 'assistant') {
        const items = [...last.items];
        const lastItem = items[items.length - 1];
        if (lastItem && lastItem.type === 'text' && !lastItem.interim) {
            items[items.length - 1] = { ...lastItem, text: lastItem.text + text };
        } else {
            items.push({ type: 'text', text });
        }
        return [...messages.slice(0, -1), { ...last, items }];
    }
    return [...messages, { role: 'assistant', items: [{ type: 'text', text }] }];
};

/** Close the current text segment; interim text (before a tool call) becomes a hidden working note. */
const markLastTextInterim = (messages: ITranscriptMessage[]): ITranscriptMessage[] => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return messages;
    const items = [...last.items];
    for (let i = items.length - 1; i >= 0; i -= 1) {
        const item = items[i];
        if (item.type === 'text') {
            items[i] = { ...item, interim: true };
            break;
        }
    }
    return [...messages.slice(0, -1), { ...last, items }];
};

const appendAssistantItem = (messages: ITranscriptMessage[], item: ITranscriptMessage['items'][number]): ITranscriptMessage[] => {
    const last = messages[messages.length - 1];
    if (last && last.role === 'assistant') {
        return [...messages.slice(0, -1), { ...last, items: [...last.items, item] }];
    }
    return [...messages, { role: 'assistant', items: [item] }];
};

export const useAssistant = (open: boolean) => {
    const location = useLocation();
    const [state, setState] = useState<IAssistantState>({
        status: null,
        conversation: null,
        history: [],
        streaming: false,
        pending: null,
        error: null,
        activity: IDLE,
        attachments: [],
        uploading: false,
    });
    const abortRef = useRef<AbortController | null>(null);
    // the workspace the dashboard is signed into; the only one this panel acts in
    const dashboardCompanyId = state.status?.workspace?.company_id;
    const workspaceMismatch = !!state.conversation && !conversationMatchesWorkspace(state.conversation.company_id, dashboardCompanyId);

    const loadStatus = useCallback(async () => {
        try {
            const status = await assistantApi.status();
            setState(s => ({ ...s, status: status.data }));
        } catch (e) {
            setState(s => ({ ...s, status: { enabled: false, model: '', workspace: null as never, playbooks: [] } }));
        }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            const res = await assistantApi.list();
            setState(s => ({ ...s, history: res.data.conversations }));
        } catch (e) {
            /* history is optional */
        }
    }, []);

    // Status decides whether the button renders at all, so load it on mount;
    // history only when the drawer opens.
    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        if (open) loadHistory();
    }, [open, loadHistory]);

    const openConversation = useCallback(async (id: string) => {
        const res = await assistantApi.get(id);
        setState(s => ({ ...s, conversation: res.data, pending: res.data.pending_action?.actions || null, error: null, attachments: [] }));
    }, []);

    const newConversation = useCallback(async () => {
        const res = await assistantApi.create();
        setState(s => ({ ...s, conversation: res.data, pending: null, error: null, attachments: [] }));
        loadHistory();
        return res.data;
    }, [loadHistory]);

    const handleEvent = useCallback((event: TAssistantEvent) => {
        setState(s => {
            if (!s.conversation) return s;
            let { messages } = s.conversation;
            let { pending, error, conversation, activity } = s;
            switch (event.type) {
                case 'text_delta':
                    messages = appendAssistantText(messages, event.text);
                    if (activity.phase !== 'answering') activity = { ...activity, phase: 'answering', label: 'Writing answer' };
                    break;
                case 'text_end':
                    if (event.interim) {
                        messages = markLastTextInterim(messages);
                        activity = { ...activity, phase: 'thinking', label: 'Thinking' };
                    }
                    break;
                case 'tool_call':
                    messages = appendAssistantItem(messages, {
                        type: 'tool_call',
                        id: event.id,
                        name: event.name,
                        label: event.label,
                        input: event.input,
                    });
                    activity = { ...activity, phase: 'tool', label: event.label || event.name, steps: activity.steps + 1 };
                    break;
                case 'tool_result':
                    messages = appendAssistantItem(messages, {
                        type: 'tool_result',
                        tool_use_id: event.tool_use_id,
                        is_error: event.is_error,
                        text: event.text,
                        label: event.label,
                    });
                    activity = { ...activity, phase: 'thinking', label: 'Thinking' };
                    break;
                case 'confirm_required':
                    pending = event.actions;
                    break;
                case 'choices':
                    messages = appendAssistantItem(messages, {
                        type: 'choices',
                        id: event.id,
                        question: event.question,
                        options: event.options,
                    });
                    break;
                case 'suggestions':
                    messages = appendAssistantItem(messages, { type: 'suggestions', id: event.id, options: event.options });
                    break;
                case 'file':
                    messages = appendAssistantItem(messages, {
                        type: 'file',
                        id: event.id,
                        name: event.name,
                        size: event.size,
                        url: event.url,
                    });
                    break;
                case 'workspace_changed':
                    conversation = { ...conversation, workspace: event.workspace, company_id: event.workspace.company_id };
                    break;
                case 'turn_complete':
                    // the stored id of the answer, so the thumbs can point at it right away
                    messages = tagLastAssistantMessage(messages, event.message_id);
                    break;
                case 'error':
                    error = event.message;
                    break;
                default:
                    break;
            }
            return { ...s, pending, error, activity, conversation: { ...conversation, messages } };
        });
    }, []);

    const runStream = useCallback(
        async (path: string, body: Record<string, unknown>, initialLabel = 'Thinking') => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setState(s => ({
                ...s,
                streaming: true,
                error: null,
                activity: { phase: 'thinking', label: initialLabel, startedAt: Date.now(), steps: 0 },
            }));
            try {
                await streamAssistant(path, body, handleEvent, controller.signal);
            } catch (e) {
                if ((e as Error).name !== 'AbortError') {
                    setState(s => ({ ...s, error: 'Lost the connection to Swiftlane. Please try again.' }));
                }
            } finally {
                setState(s => ({ ...s, streaming: false, activity: { ...s.activity, phase: 'idle' } }));
                loadHistory();
            }
        },
        [handleEvent, loadHistory],
    );

    const send = useCallback(
        async (message: string, entity?: IAssistantEntity) => {
            const text = message.trim();
            if (!text || state.streaming) return;
            let { conversation } = state;
            if (!conversation) {
                conversation = await newConversation();
            }
            if (!conversationMatchesWorkspace(conversation.company_id, dashboardCompanyId)) {
                setState(s => ({ ...s, error: WORKSPACE_MISMATCH }));
                return;
            }
            // a note the dashboard sends on the admin's behalf renders as a quiet system line
            const item: ITranscriptMessage['items'][number] = text.startsWith(SYSTEM_NOTE_PREFIX)
                ? { type: 'system', text: text.slice(SYSTEM_NOTE_PREFIX.length) }
                : { type: 'text', text };
            const { attachments } = state;
            const items: ITranscriptMessage['items'] = [...attachments.map(a => ({ type: 'attachment' as const, name: a.name })), item];
            setState(s => ({
                ...s,
                attachments: [],
                conversation: s.conversation
                    ? { ...s.conversation, messages: [...s.conversation.messages, { role: 'user', items }] }
                    : s.conversation,
            }));
            await runStream(
                `conversations/${conversation.id}/messages`,
                {
                    message: text,
                    // company_id lets the API refuse a turn when this page is in another workspace
                    context: { page: document.title, url: location.pathname, entity, company_id: dashboardCompanyId },
                    attachment_ids: attachments.map(a => a.id),
                },
                attachments.length ? `Reading ${attachments.length === 1 ? attachments[0].name : 'the files'}` : 'Thinking',
            );
        },
        [state, runStream, location.pathname, newConversation, dashboardCompanyId],
    );

    const confirm = useCallback(
        async (approve: boolean) => {
            if (!state.conversation || !state.pending) return;
            if (!conversationMatchesWorkspace(state.conversation.company_id, dashboardCompanyId)) {
                setState(s => ({ ...s, error: WORKSPACE_MISMATCH }));
                return;
            }
            setState(s => ({ ...s, pending: null }));
            await runStream(
                `conversations/${state.conversation.id}/confirm`,
                confirmBody(approve, false, { company_id: dashboardCompanyId }),
            );
        },
        [state.conversation, state.pending, runStream, dashboardCompanyId],
    );

    /** The create was opened in the real dashboard form instead; tell the model, run nothing. */
    const handoff = useCallback(
        async (action: IPendingAction) => {
            if (!state.conversation || !state.pending || !action.form) return;
            if (!conversationMatchesWorkspace(state.conversation.company_id, dashboardCompanyId)) {
                setState(s => ({ ...s, error: WORKSPACE_MISMATCH }));
                return;
            }
            const card: ITranscriptMessage['items'][number] = {
                type: 'handoff',
                label: action.form.label,
                route: action.form.route,
                fields: action.form.fields,
            };
            setState(s => ({
                ...s,
                pending: null,
                conversation: s.conversation
                    ? { ...s.conversation, messages: appendAssistantItem(s.conversation.messages, card) }
                    : s.conversation,
            }));
            await runStream(`conversations/${state.conversation.id}/confirm`, confirmBody(false, true, { company_id: dashboardCompanyId }));
        },
        [state.conversation, state.pending, runStream, dashboardCompanyId],
    );

    /** Upload now so the server can reject bad files before the message is written. */
    const attach = useCallback(
        async (file: File) => {
            let { conversation } = state;
            if (!conversation) {
                conversation = await newConversation();
            }
            setState(s => ({ ...s, uploading: true, error: null }));
            try {
                const res = await uploadFile(conversation.id, file);
                setState(s => ({ ...s, uploading: false, attachments: [...s.attachments, res.data] }));
            } catch (e) {
                const message = (e as { response?: { data?: { data?: { msg?: string } } } })?.response?.data?.data?.msg;
                setState(s => ({ ...s, uploading: false, error: message || `Could not upload ${file.name}.` }));
            }
        },
        [state, newConversation],
    );

    const removeAttachment = useCallback((id: string) => {
        setState(s => ({ ...s, attachments: s.attachments.filter(a => a.id !== id) }));
    }, []);

    /** Thumbs on an answer: shown immediately, reverted with a message if the save fails. */
    const rate = useCallback(
        async (messageId: string, feedback: IFeedback) => {
            const conversationId = state.conversation?.id;
            if (!conversationId) return;
            const previous = state.conversation?.messages.find(m => m.id === messageId)?.feedback ?? null;
            const apply = (value: IFeedback | null) =>
                setState(s => ({
                    ...s,
                    conversation: s.conversation
                        ? { ...s.conversation, messages: withFeedback(s.conversation.messages, messageId, value) }
                        : s.conversation,
                }));
            apply(feedback);
            try {
                await assistantApi.rate(conversationId, messageId, feedback);
            } catch (e) {
                apply(previous);
                setState(s => ({ ...s, error: 'Could not save your feedback. Please try again.' }));
            }
        },
        [state.conversation],
    );

    const stop = useCallback(() => abortRef.current?.abort(), []);

    return {
        ...state,
        send,
        confirm,
        handoff,
        attach,
        removeAttachment,
        rate,
        workspaceMismatch,
        newConversation,
        openConversation,
        stop,
        reload: loadStatus,
    };
};
